/**
 * Admin: Change Local Pickup → Delivery modal.
 *
 * Mirrors the customer modal but uses the admin PATCH endpoint and
 * exposes two extra controls:
 *   · An `admin_note` free-text field (persisted on the audit event).
 *   · A `confirm_preparing_reset` checkbox required to change delivery
 *     while the order is already in `preparing`. Backend refuses the
 *     change without this confirmation.
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Truck, Package, Loader2, X, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import {
  SHIPPING_OPTIONS, computeChangePreview,
} from "@/components/tracker/ChangeToDeliveryModal";

const USPS_ACK_TEXT =
  "I understand that USPS Stamp shipping has no tracking and I accept the risk of loss or non-delivery.";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function AdminChangeDeliveryModal({
  open, onClose, order, onSaved,
}) {
  const [code, setCode] = useState("usps_stamp");
  const [ack, setAck] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [adminNote, setAdminNote] = useState("");
  const existingAddr = (order?.customer?.shipping_address) || {};
  const [line1, setLine1] = useState(existingAddr.line1 || "");
  const [line2, setLine2] = useState(existingAddr.line2 || "");
  const [city, setCity] = useState(existingAddr.city || "");
  const [state, setState] = useState(existingAddr.state || "");
  const [postalCode, setPostalCode] = useState(existingAddr.postal_code || "");
  const [country, setCountry] = useState(existingAddr.country || "United States");
  const [busy, setBusy] = useState(false);

  const opt = useMemo(() => SHIPPING_OPTIONS.find((o) => o.code === code), [code]);
  const preview = useMemo(() => computeChangePreview(order, code), [order, code]);
  const isPreparing = order?.status === "preparing";

  const canSubmit =
    !!line1.trim() && !!city.trim() && !!state.trim() &&
    !!postalCode.trim() && !!country.trim() &&
    (!opt.requiresAck || ack) &&
    (!isPreparing || confirmReset) && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const r = await api.patch(`/admin/orders/${order.public_id}/delivery-method`, {
        delivery_code: code,
        shipping_address: {
          line1: line1.trim(),
          line2: line2.trim() || null,
          city: city.trim(),
          state: state.trim(),
          postal_code: postalCode.trim(),
          country: country.trim(),
        },
        usps_acknowledgment: ack,
        admin_note: adminNote.trim() || null,
        confirm_preparing_reset: confirmReset,
      });
      const applied = r.data?.delivery_change?.already_applied;
      toast.success(applied
        ? "Delivery method already set — no charge applied."
        : "Delivery method updated.");
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail)
        || "Could not update the delivery method.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog" aria-modal="true"
      data-testid="admin-change-delivery-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] overflow-y-auto border-4 border-[var(--ink)]">
        <div className="flex items-start justify-between p-5 md:p-6 border-b-2 border-[var(--ink)]">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">
              Admin · Change delivery method
            </div>
            <h2 className="font-display text-2xl md:text-3xl mt-1">Convert Local Pickup to Delivery</h2>
          </div>
          <button
            type="button" onClick={onClose}
            className="text-[var(--smoke)] hover:text-[var(--ink)] p-1"
            data-testid="admin-change-delivery-close"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 md:p-6 space-y-5">
          {isPreparing && (
            <div className="p-3 rounded-lg border-2 border-amber-700 bg-amber-50 text-sm">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="mt-0.5 text-amber-800" />
                <div>
                  <div className="font-bold">Order is already in Preparing.</div>
                  <div>
                    Changing delivery now resets the fulfillment workflow.
                    Confirm below to proceed.
                  </div>
                </div>
              </div>
              <label className="mt-3 flex items-center gap-2 cursor-pointer"
                     data-testid="admin-change-delivery-preparing-ack-label">
                <input
                  type="checkbox" checked={confirmReset}
                  onChange={(e) => setConfirmReset(e.target.checked)}
                  data-testid="admin-change-delivery-preparing-ack"
                />
                <span>I confirm the fulfillment workflow reset.</span>
              </label>
            </div>
          )}

          {/* Method picker */}
          <fieldset className="space-y-3">
            <legend className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">
              Shipping method
            </legend>
            {SHIPPING_OPTIONS.map((o) => (
              <label
                key={o.code}
                className={
                  "block p-4 rounded-xl border-2 cursor-pointer transition " +
                  (code === o.code
                    ? "border-[var(--panini-red)] bg-red-50"
                    : "border-[var(--stone)] hover:border-[var(--ink)]")
                }
                data-testid={`admin-change-delivery-option-${o.code}`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="radio" name="delivery_code" value={o.code}
                    checked={code === o.code}
                    onChange={() => { setCode(o.code); if (!o.requiresAck) setAck(false); }}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <div className="font-bold flex items-center gap-2">
                        {o.code === "usps_stamp" ? <Package size={16} /> : <Truck size={16} />}
                        {o.label}
                      </div>
                      <div className="font-mono font-bold">{fmt(o.price)}</div>
                    </div>
                    <p className="text-xs text-[var(--smoke)] mt-1">{o.desc}</p>
                    {o.notice && (
                      <p className="text-xs font-bold text-amber-800 mt-1">{o.notice}</p>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </fieldset>

          {opt.requiresAck && (
            <label className="flex items-start gap-2 p-3 border-2 border-amber-700 bg-amber-50 rounded-lg text-sm cursor-pointer"
                   data-testid="admin-change-delivery-usps-ack-label">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                     className="mt-1"
                     data-testid="admin-change-delivery-usps-ack" />
              <span>{USPS_ACK_TEXT}</span>
            </label>
          )}

          <fieldset className="space-y-3">
            <legend className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">
              Shipping address
            </legend>
            <input type="text" placeholder="Address line 1" value={line1}
                   onChange={(e) => setLine1(e.target.value)}
                   className="w-full p-2 border-2 border-[var(--stone)] rounded-lg text-sm"
                   data-testid="admin-change-delivery-line1" required />
            <input type="text" placeholder="Address line 2 (optional)" value={line2}
                   onChange={(e) => setLine2(e.target.value)}
                   className="w-full p-2 border-2 border-[var(--stone)] rounded-lg text-sm" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input type="text" placeholder="City" value={city}
                     onChange={(e) => setCity(e.target.value)}
                     className="p-2 border-2 border-[var(--stone)] rounded-lg text-sm"
                     required />
              <input type="text" placeholder="State" value={state}
                     onChange={(e) => setState(e.target.value)}
                     className="p-2 border-2 border-[var(--stone)] rounded-lg text-sm"
                     required />
              <input type="text" placeholder="ZIP code" value={postalCode}
                     onChange={(e) => setPostalCode(e.target.value)}
                     className="p-2 border-2 border-[var(--stone)] rounded-lg text-sm"
                     required />
            </div>
            <input type="text" placeholder="Country" value={country}
                   onChange={(e) => setCountry(e.target.value)}
                   className="w-full p-2 border-2 border-[var(--stone)] rounded-lg text-sm"
                   required />
          </fieldset>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]"
                   htmlFor="admin-change-delivery-note">
              Admin note (optional)
            </label>
            <textarea
              id="admin-change-delivery-note"
              value={adminNote} onChange={(e) => setAdminNote(e.target.value)}
              rows={2} maxLength={500}
              className="w-full mt-1 p-2 border-2 border-[var(--stone)] rounded-lg text-sm"
              placeholder="Reason for the change (recorded on the audit log)"
              data-testid="admin-change-delivery-note"
            />
          </div>

          {preview && (
            <div className="p-4 bg-slate-50 border-2 border-[var(--ink)] rounded-xl text-sm space-y-1"
                 data-testid="admin-change-delivery-preview">
              <div className="flex justify-between">
                <span>Current delivery</span>
                <span className="font-mono">Local Pickup</span>
              </div>
              <div className="flex justify-between">
                <span>New delivery method</span>
                <span className="font-mono">{opt.label}</span>
              </div>
              <div className="flex justify-between">
                <span>Original total</span>
                <span className="font-mono">{fmt(preview.originalTotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>Added shipping</span>
                <span className="font-mono text-amber-800 font-bold">+{fmt(preview.addedShipping)}</span>
              </div>
              <div className="flex justify-between border-t-2 border-[var(--ink)] pt-1 mt-1">
                <span className="font-bold">New total</span>
                <span className="font-mono font-bold">{fmt(preview.newTotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>Amount already paid</span>
                <span className="font-mono">{fmt(preview.amountPaid)}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-bold">New balance due</span>
                <span className="font-mono font-bold text-[var(--panini-red)]">{fmt(preview.balanceDue)}</span>
              </div>
            </div>
          )}

          <div className="flex flex-col-reverse md:flex-row gap-3 md:justify-end pt-2">
            <button type="button" onClick={onClose} disabled={busy}
                    className="btn-ghost"
                    data-testid="admin-change-delivery-cancel">
              Cancel
            </button>
            <button type="submit" disabled={!canSubmit}
                    className="btn bg-[var(--panini-red)] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="admin-change-delivery-confirm">
              {busy ? <Loader2 className="animate-spin" size={16} /> : null}
              Confirm Delivery Change
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
